'use client';

import type { GameListItemDto } from '@/queries/games/games.schema';
import { useTranslations } from 'next-intl';
import GameCard from '@/components/entities/games/GameCard';
import GameSkeleton from '@/components/entities/games/GameCard/components/GameSkeleton';
import { SkeletonParent } from '@/components/ui/Skeleton/skeleton';
import { AppRoutes } from '@/i18n/constants';
import { cn } from '@/utils/cn';
import EmptyGamesState from '../EmptyGamesState';
import ResentSearches from '../ResentSearches';
import TopGamesSwiper from './components/TopGamesSwiper';

interface Props {
  recentSearch: string;
  gameList: GameListItemDto[];
  isLoadingGames: boolean;
  searchValue: string;
}

const DesktopSearchDropdown = ({
  recentSearch,
  gameList,
  isLoadingGames,
  searchValue,
}: Props) => {
  const t = useTranslations('lobby.filters');

  const hasSearchValue = Boolean(searchValue);
  const hasGames = gameList.length > 0;
  const showEmptyState = hasSearchValue && !isLoadingGames && !hasGames;
  const showResults = hasSearchValue && !isLoadingGames && hasGames;

  const skeletonCards = Array.from({ length: 5 }).map((_, index) => (
    <GameSkeleton key={index} className="px-0 py-1 h-42 w-30 rounded-2xl" />
  ));

  return (
    <div
      className={cn(
        'hidden lg:flex min-h-18 left-0 overflow-y-auto top-full max-h-170.5 z-50 lg:z-30 mt-1 py-3.5 lg:bg-main-800 hide-scrollbar rounded-2xl shadow-[0_8px_100px_0_var(--color-c1000)] px-2 lg:p-6 lg:pb-5.5 w-full absolute flex-col',
        showEmptyState && 'lg:pt-0',
      )}
    >
      {hasSearchValue && isLoadingGames && (
        <div className="lg:mb-4.25 mb-6.5">
          <SkeletonParent className="p-0 h-5.5 w-32 rounded-xl mb-3.75 lg:mb-2.75" />
          <div className="flex flex-wrap gap-4.25 w-full">{skeletonCards}</div>
        </div>
      )}

      {showResults && (
        <div className="lg:mb-4.25 mb-6.5">
          <h2 className="text-white font-medium text-lg leading-5.5 mb-3.75 lg:mb-2.75">
            {t('search-results')}
          </h2>
          <div className="flex flex-wrap gap-4.25 w-full">
            {gameList.map(game => (
              <GameCard
                key={game.id}
                id={game.id}
                name={game.name}
                likeButtonIconSize="compactLg"
                likeButtonPosition="compactLg"
                cover={game.cover}
                path={AppRoutes.gamesId(game.id)}
              />
            ))}
          </div>

        </div>
      )}

      {showEmptyState && (
        <EmptyGamesState className="lg:mt-0 lg:gap-0.5" />
      )}

      {!hasGames && !isLoadingGames && (
        <ResentSearches recentSearch={recentSearch} />
      )}

      <TopGamesSwiper />
    </div>
  );
};

export default DesktopSearchDropdown;
